import { graphql } from '@/generated/gql'
import { useLazyQuery } from '@apollo/client'

import { useEffect } from 'react'

const menuPageUseExistingOrderGetGmOrderQuery = graphql(`
  query MenuPageUseExistingOrder_GetGmOrderQuery($gmOrderId: ID!) {
    gmOrder(id: $gmOrderId) {
      id
      orderId
      vendor {
        id
      }
      orderItems {
        items {
          isCustom
          id
        }
      }
      customName
      isCateringTeamOrder
      user {
        id
        fullName
      }
      ...BasketUiCompatibleGmOrder_OrderDataFragment
      ...SupplierUseExistingOrderItems_OrderDataFragment
      ...UseInputRelatedOrderData_OrderDataFragment
      ...UseExistingOrderMutations_OrderDataFragment
      ...UseOrderPaymentType_OrderDataFragment
      ...OrderBasketMapping_OrderDataFragment
    }
  }
`)

type Props = {
  orderId?: string
  vendorId?: string
}

export const useExistingOrderQueries = ({ orderId, vendorId }: Props) => {
  const [getOrderData, { data, loading, called, error, variables }] = useLazyQuery(
    menuPageUseExistingOrderGetGmOrderQuery,
    { fetchPolicy: 'cache-and-network' },
  )

  useEffect(() => {
    if (!orderId || !vendorId) return
    getOrderData({ variables: { gmOrderId: orderId } })
  }, [orderId, getOrderData, vendorId])

  const orderData = data?.gmOrder && data.gmOrder.vendor.id === vendorId ? data.gmOrder : null
  // Apollo keeps the previous result until the next request starts, so a failure only counts
  // for the order the URL currently names (and never once the orderId has been cleared).
  const loadFailed =
    Boolean(orderId) &&
    called &&
    !loading &&
    variables?.gmOrderId === orderId &&
    (Boolean(error) || !orderData)

  return {
    loading,
    called,
    orderData,
    loadFailed,
  }
}

export type UseExistingOrderQueriesResult = ReturnType<typeof useExistingOrderQueries>
